import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "media-asset-management",
  kind: "domain-vertical",
  title: "Media Asset Management Systems",
  shortTitle: "Media Management",
  status: "draft",
  tier: "we-do",
  seoTitle: "Media Asset Management Systems | General Applications",
  seoDescription:
    "Media asset management systems connecting masters, derivatives, metadata, rights, review, lifecycle, search, and archival control.",
  summary:
    "Media asset management systems that connect masters, proxies, metadata, rights, review, delivery, and archival state so a collection remains usable as it grows.",
  image: "/images/blog/reading-a-data-module-code-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["media-entertainment"],
  relatedPostSlugs: [
    "search-relevance-taxonomy",
    "reading-a-data-module-code",
    "multi-terabyte-ingest-choke",
  ],
  engineering: [
    "Asset, master, proxy, derivative, project, and collection relationship models",
    "Metadata, identifier, rights, access, version, and lifecycle controls",
    "Ingest, review, approval, delivery, archive, and disposition workflows",
    "Search, browse, facets, and contextual discovery across large collections",
    "Storage-platform, library, production, and delivery-system integration",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Assets Need Context to Be Useful",
    },
    {
      type: "paragraph",
      text: "A master file, preview, transcript, subtitle, artwork, rights record, and delivery package may all belong to one piece of work. A media asset management system makes those relationships explicit so people can find the right version, understand what it is for, and move it through review, delivery, or preservation without reconstructing the answer from file names.",
    },
    {
      type: "heading",
      level: 2,
      text: "The Collection Must Survive Its Workflow",
    },
    {
      type: "paragraph",
      text: "Production, delivery, and archive are not separate piles of files. They are lifecycle states with different access, rights, quality, and retention requirements. The system should retain the source context and decision history that let the next person work with the asset without relying on the person who created it.",
    },
  ],
  phases: {
    study: {
      focus:
        "Map the assets, metadata, ownership, review, rights, delivery, storage, and archival decisions that the system must connect.",
      produces: [
        "Asset, collection, relationship, and workflow inventory",
        "Metadata, identifier, version, rights, and lifecycle assessment",
        "Source-system, storage, search, and integration map",
        "User, role, access, and stewardship decision model",
      ],
      fit: {
        good: [
          "Media teams managing growing collections across shared storage, production tools, review processes, and delivery channels",
          "Organizations that need a durable source of truth for what an asset is, where it belongs, who may use it, and which version is authoritative",
        ],
        poor: [
          "A file migration with no ownership, metadata, lifecycle, access, or discovery requirement",
          "A request to automate rights or approval decisions without identifying their authoritative source and responsible owner",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the asset, metadata, relationship, lifecycle, access, and discovery model before selecting or configuring a platform.",
      produces: [
        "Media asset and collection architecture",
        "Metadata, version, rights, access, and lifecycle model",
        "Ingest, review, approval, delivery, and archive workflow design",
        "Search, browse, integration, and stewardship approach",
      ],
    },
    "detailed-design": {
      focus:
        "Specify records, interfaces, integrations, validation, and exception paths that make the collection workable for both production and stewardship.",
      produces: [
        "Asset, metadata, relationship, and lifecycle specifications",
        "Ingest, review, delivery, archive, and exception procedures",
        "Search, access, rights, and permission-interface definitions",
        "Storage, production-tool, and delivery integration contracts",
      ],
    },
    implementation: {
      focus:
        "Build or modernize the system around representative assets and verify that people can find, interpret, govern, and move them through their lifecycle.",
      produces: [
        "Connected asset, metadata, lifecycle, and relationship records",
        "Production, review, delivery, and archive workflows",
        "Search and management interfaces with source context",
        "Operational stewardship and adoption guidance",
      ],
    },
  },
};
